import { CarFront, ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, EmptyState, Eyebrow } from '@/shared/components';
import { useApi } from '@/shared/api';
import { ParkingListLoadError } from '@/features/property-groups/parking-occupancy/ParkingListLoadError';
import { useApartmentDetail } from './ApartmentDetailLayout';
import { ParkingSpotCard } from './ParkingSpotCard';

export function ParkingTab() {
  const { propertyId, propertyGroupId } = useApartmentDetail();
  const api = useApi();
  const spots = useQuery({
    queryKey: ['property-groups', propertyGroupId, 'parking-spots'],
    queryFn: () => api.parking.listSpots(propertyGroupId!),
    enabled: !!propertyGroupId,
  });

  if (!propertyGroupId) {
    return <EmptyState icon="Error" title="Dem Apartment ist kein Objekt zugeordnet" />;
  }
  if (spots.isLoading) return <p className="text-ink-soft text-sm">Lade Parkplätze…</p>;
  if (spots.isError || !spots.data) {
    return (
      <ParkingListLoadError query={spots} fallback="Parkplätze konnten nicht geladen werden." />
    );
  }

  const assignedSpots = spots.data.spots.filter((spot) =>
    spot.assignedPropertyIds.includes(propertyId),
  );
  return (
    <div className="space-y-4">
      <Card variant="alt">
        <CardBody className="flex flex-wrap items-center justify-between gap-4 py-5">
          <div>
            <Eyebrow variant="muted">Apartment-Zuweisung</Eyebrow>
            <p className="text-ink-soft mt-2 text-sm">
              Gemeinsame Stellplätze werden zentral auf Objektebene gepflegt.
            </p>
          </div>
          <a
            href="/parking"
            className="text-sealwax inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline"
          >
            Parkplätze zentral verwalten
            <ExternalLink size={14} strokeWidth={1.6} />
          </a>
        </CardBody>
      </Card>

      {assignedSpots.length === 0 ? (
        <EmptyState
          icon="Pin"
          title="Keine Parkplätze zugewiesen"
          description="Weisen Sie diesem Apartment einen Stellplatz in der zentralen Objektverwaltung zu."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {assignedSpots.map((spot) => (
            <ParkingSpotCard key={spot.id} spot={spot} currency={spot.currency} />
          ))}
        </div>
      )}
      {!spots.data.paymentAvailable && assignedSpots.some((spot) => spot.price > 0) && (
        <p className="text-ink-soft flex items-center gap-2 text-sm">
          <CarFront size={16} strokeWidth={1.6} />
          Kostenpflichtige Stellplätze werden online buchbar, sobald Stripe verbunden ist.
        </p>
      )}
    </div>
  );
}
